import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Pen, AlertTriangle } from 'lucide-react';

/**
 * Formats a date string into UK format with ordinal suffix.
 * e.g. "2026-03-12" -> "12th March 2026"
 */
const formatDateUK = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;

  const day = date.getDate();
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  const suffix = (d) => {
    if (d > 3 && d < 21) return 'th';
    switch (d % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  return `${day}${suffix(day)} ${month} ${year}`;
};

/**
 * Formats a number as GBP currency.
 * e.g. 125.5 -> "£125.50"
 */
const formatCurrency = (amount) => {
  const num = parseFloat(amount);
  if (isNaN(num)) return '';
  return `\u00A3${num.toFixed(2)}`;
};

/**
 * SignatureModal — Full-screen mobile-friendly signing overlay for treatment reports.
 *
 * Props:
 *   report       — treatment report object
 *   onSign       — callback({ signature_data: base64PNG, signer_name: string })
 *   onClose      — close callback
 *   saving       — boolean, shows spinner on confirm button when true
 */
const SignatureModal = ({ report, onSign, onClose, saving = false }) => {
  const [signerName, setSignerName] = useState('');
  const [hasDrawn, setHasDrawn] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const lastPointRef = useRef(null);

  // ── Canvas setup & resize ──────────────────────────────────────────

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    // Save existing drawing if any
    let imageData = null;
    if (hasDrawn) {
      try {
        imageData = canvas.toDataURL('image/png');
      } catch (e) {
        // ignore
      }
    }

    canvas.width = rect.width * dpr;
    canvas.height = 200 * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = '200px';

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#1a1a2e';

    // Fill white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, 200);

    // Restore drawing if we had one
    if (imageData && hasDrawn) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, 200);
      };
      img.src = imageData;
    }
  }, [hasDrawn]);

  useEffect(() => {
    setupCanvas();
    const handleResize = () => setupCanvas();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setupCanvas]);

  // Register touch events as non-passive so preventDefault works (prevents scroll while drawing)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const opts = { passive: false };
    canvas.addEventListener('touchstart', startDrawing, opts);
    canvas.addEventListener('touchmove', draw, opts);
    canvas.addEventListener('touchend', stopDrawing, opts);
    canvas.addEventListener('touchcancel', stopDrawing, opts);
    return () => {
      canvas.removeEventListener('touchstart', startDrawing, opts);
      canvas.removeEventListener('touchmove', draw, opts);
      canvas.removeEventListener('touchend', stopDrawing, opts);
      canvas.removeEventListener('touchcancel', stopDrawing, opts);
    };
  }); // runs every render to capture latest closures

  // Prevent body scroll when modal is open
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  // ── Drawing logic ──────────────────────────────────────────────────

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();

    if (e.touches && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const point = getPoint(e);
    lastPointRef.current = point;

    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(point.x, point.y);
    }
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();

    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    const point = getPoint(e);
    const last = lastPointRef.current;

    if (last) {
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
    }

    lastPointRef.current = point;
    if (!hasDrawn) setHasDrawn(true);
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
    lastPointRef.current = null;
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, 200);
    setHasDrawn(false);
  };

  // ── Submit ─────────────────────────────────────────────────────────

  const handleConfirm = () => {
    const canvas = canvasRef.current;
    if (!canvas || !signerName.trim() || !hasDrawn) return;
    const signatureData = canvas.toDataURL('image/png');
    onSign({
      signature_data: signatureData,
      signer_name: signerName.trim()
    });
  };

  const canSubmit = signerName.trim().length > 0 && hasDrawn && !saving;

  // ── Report data helpers ────────────────────────────────────────────

  const lineItems = report?.line_items || [];
  const totalAmount = parseFloat(report?.total_amount) || 0;
  const hasTreatmentMethods = report?.treatment_methods && report.treatment_methods.trim();
  const hasPestFindings = report?.pest_findings && report.pest_findings.trim();
  const hasRecommendations = report?.recommendations && report.recommendations.trim();
  // Parse photos — can be array of {url, name} objects
  const photosList = (() => {
    if (!report?.photos) return [];
    if (Array.isArray(report.photos)) return report.photos;
    if (typeof report.photos === 'string') {
      try { const parsed = JSON.parse(report.photos); if (Array.isArray(parsed)) return parsed; } catch {}
    }
    return [];
  })();
  const hasPhotos = photosList.length > 0;
  // Parse products_used — can be JSON array of {id, name} or plain string
  const productsUsedList = (() => {
    if (!report?.products_used) return [];
    if (Array.isArray(report.products_used)) return report.products_used;
    if (typeof report.products_used === 'string') {
      try {
        const parsed = JSON.parse(report.products_used);
        if (Array.isArray(parsed)) return parsed;
      } catch {}
      return report.products_used.trim() ? [{ name: report.products_used }] : [];
    }
    return [];
  })();
  const hasProductsUsed = productsUsedList.length > 0;

  // Build address string from treatment address fields first, fallback to customer address
  const treatmentAddrParts = [
    report?.treatment_address_line1,
    report?.treatment_address_line2,
    report?.treatment_address_city,
    [report?.treatment_address_state, report?.treatment_address_postcode].filter(Boolean).join(' ')
  ].filter(Boolean);
  const addressStr = treatmentAddrParts.length > 0
    ? treatmentAddrParts.join(', ')
    : (report?.customer_address || '');

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
      <div className="bg-white w-full h-full md:h-auto md:max-w-2xl md:max-h-[95vh] md:rounded-xl md:shadow-2xl flex flex-col overflow-hidden">

        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-pest-blue to-pest-teal px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 text-white">
            <Pen className="w-5 h-5" />
            <h2 className="text-lg font-semibold">Sign Treatment Report</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors p-1"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* ── Scrollable body ─────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">

          {/* 1. Report Summary — styled like the actual document */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            {/* Document header with logo */}
            <div className="px-4 pt-3 pb-1">
              <img src="/logo.png" alt="PestControl.co.uk" className="h-10" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>

            <div className="px-4 py-3">
              <h3 className="text-lg font-bold text-gray-800 mb-3">Treatment Report</h3>

              {/* Report details grid */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs mb-3">
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-500 font-medium">Report No.</span>
                    <span className="font-semibold text-gray-800">{report?.report_number || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 font-medium">Service Date</span>
                    <span className="text-gray-800">{formatDateUK(report?.service_date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 font-medium">Technician</span>
                    <span className="text-gray-800">{report?.technician_name || '-'}</span>
                  </div>
                </div>
                <div className="space-y-0.5">
                  <span className="text-gray-500 font-medium text-xs">Treatment carried out at:</span>
                  {report?.treatment_contact_name ? (
                    <p className="text-gray-800 text-xs font-medium">{report.treatment_contact_name}</p>
                  ) : (
                    <p className="text-gray-800 text-xs font-medium">{report?.customer_name || '-'}</p>
                  )}
                  {(report?.treatment_contact_company || report?.customer_company) && (
                    <p className="text-gray-600 text-xs">{report.treatment_contact_company || report.customer_company}</p>
                  )}
                  {addressStr && <p className="text-gray-600 text-xs">{addressStr}</p>}
                  {report?.treatment_contact_phone && (
                    <p className="text-gray-600 text-xs">{report.treatment_contact_phone}</p>
                  )}
                  {report?.treatment_contact_email && (
                    <p className="text-gray-600 text-xs">{report.treatment_contact_email}</p>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div className="h-0.5 bg-gradient-to-r from-pest-blue to-pest-teal mb-3"></div>

              {/* Treatment details */}
              {(hasTreatmentMethods || hasPestFindings || hasRecommendations || hasProductsUsed) && (
                <div className="space-y-2.5 text-xs mb-3">
                  {hasTreatmentMethods && (
                    <div>
                      <h4 className="font-semibold text-gray-700 uppercase tracking-wider text-[10px] mb-0.5">Treatment Methods</h4>
                      <p className="text-gray-700 whitespace-pre-line leading-relaxed">{report.treatment_methods}</p>
                    </div>
                  )}
                  {hasPestFindings && (
                    <div>
                      <h4 className="font-semibold text-gray-700 uppercase tracking-wider text-[10px] mb-0.5">Pest Findings</h4>
                      <p className="text-gray-700 whitespace-pre-line leading-relaxed">{report.pest_findings}</p>
                    </div>
                  )}
                  {hasProductsUsed && (
                    <div>
                      <h4 className="font-semibold text-gray-700 uppercase tracking-wider text-[10px] mb-0.5">Products Used</h4>
                      <ul className="text-gray-700 leading-relaxed list-disc list-inside">
                        {productsUsedList.map((p, i) => (
                          <li key={i}>
                            {typeof p === 'string' ? p : p.name}
                            {p.qty && p.qty > 1 ? ` (x${p.qty})` : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {hasRecommendations && (
                    <div>
                      <h4 className="font-semibold text-gray-700 uppercase tracking-wider text-[10px] mb-0.5">Recommendations</h4>
                      <p className="text-gray-700 whitespace-pre-line leading-relaxed">{report.recommendations}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Photos */}
              {hasPhotos && (
                <div className="mb-3">
                  <h4 className="font-semibold text-gray-700 uppercase tracking-wider text-[10px] mb-1">Site Photos</h4>
                  <div className="flex flex-wrap gap-2">
                    {photosList.map((photo, i) => (
                      <img
                        key={i}
                        src={photo.url || photo.data}
                        alt={photo.name || `Photo ${i + 1}`}
                        className="w-20 h-20 object-cover rounded border border-gray-200"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Line items table */}
              {lineItems.length > 0 && (
                <div className="mb-3">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-100 text-gray-600">
                        <th className="text-left font-semibold py-1.5 px-2">ITEM</th>
                        <th className="text-center font-semibold py-1.5 px-1 w-10">QTY</th>
                        <th className="text-right font-semibold py-1.5 px-1 w-14">RATE</th>
                        <th className="text-right font-semibold py-1.5 px-2 w-16">AMOUNT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((item, idx) => (
                        <tr key={idx} className="border-b border-gray-100">
                          <td className="py-1.5 px-2 text-gray-800">
                            <span className="font-medium">{item.product_service || item.product || item.name || '-'}</span>
                            {item.description && <p className="text-gray-500 text-[10px] mt-0.5">{item.description}</p>}
                          </td>
                          <td className="py-1.5 px-1 text-center text-gray-800">{item.quantity || item.qty || 1}</td>
                          <td className="py-1.5 px-1 text-right text-gray-800">{formatCurrency(item.rate || item.unit_price || 0)}</td>
                          <td className="py-1.5 px-2 text-right font-medium text-gray-800">{formatCurrency(item.amount || item.total || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Financial summary */}
              {totalAmount > 0 && (
                <div className="flex flex-col items-end text-xs space-y-0.5">
                  {report?.subtotal != null && (
                    <div className="flex gap-4">
                      <span className="text-gray-500">Subtotal</span>
                      <span className="text-gray-800 w-16 text-right">{formatCurrency(report.subtotal)}</span>
                    </div>
                  )}
                  {report?.tax_amount != null && parseFloat(report.tax_amount) > 0 && (
                    <div className="flex gap-4">
                      <span className="text-gray-500">VAT</span>
                      <span className="text-gray-800 w-16 text-right">{formatCurrency(report.tax_amount)}</span>
                    </div>
                  )}
                  <div className="flex gap-4 pt-1 border-t border-gray-200">
                    <span className="font-bold text-gray-800">TOTAL</span>
                    <span className="font-bold text-gray-800 w-16 text-right">{formatCurrency(totalAmount)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 2. Health & Safety Notice */}
          <div className="border border-red-800 rounded-lg p-3">
            <h3 className="text-sm font-bold text-red-800 mb-1 flex items-center gap-1.5"><AlertTriangle size={16} /> IMPORTANT NOTICE</h3>
            <ol className="text-xs text-red-800 space-y-1 list-decimal list-inside leading-relaxed">
              <li>Do NOT touch or move any poisons, chemicals or equipment left on site.</li>
              <li>KEEP CHILDREN, VULNERABLE ADULTS &amp; PETS AWAY from areas treated with POISONS until removed by our technician.</li>
              <li>KEEP ALL PERSONS &amp; PETS AWAY from areas treated with CHEMICALS until dried.</li>
              <li>In case of accidental ingestion, dial 999 immediately.</li>
            </ol>
          </div>

          {/* 3. Signer Name Input */}
          <div>
            <label htmlFor="signer-name" className="block text-sm font-medium text-gray-700 mb-1">
              Your Name (printed) <span className="text-red-500">*</span>
            </label>
            <input
              id="signer-name"
              type="text"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Enter your full name"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pest-teal focus:border-transparent"
              autoComplete="name"
            />
          </div>

          {/* 4. Signature Pad */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Please sign below <span className="text-red-500">*</span>
            </label>
            <div ref={containerRef} className="w-full">
              <canvas
                ref={canvasRef}
                className="w-full border border-gray-300 rounded-lg cursor-crosshair bg-white"
                style={{ touchAction: 'none', height: '200px' }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
              />
            </div>
            <button
              type="button"
              onClick={clearSignature}
              className="mt-1 text-xs text-gray-500 hover:text-red-500 transition-colors underline"
            >
              Clear Signature
            </button>
          </div>
        </div>

        {/* ── Action Buttons ──────────────────────────────────────── */}
        <div className="flex-shrink-0 border-t border-gray-200 px-4 py-3 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canSubmit}
            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-colors flex items-center justify-center gap-2 ${
              canSubmit
                ? 'bg-gradient-to-r from-pest-blue to-pest-teal hover:opacity-90'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving...
              </>
            ) : (
              <>
                <Pen className="w-4 h-4" />
                Confirm &amp; Sign
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignatureModal;
